import React, { useEffect, useRef, useState } from "react";
import GlassPanel from "@/components/GlassPanel";
import AvatarCropper from "@/components/AvatarCropper";
import PasswordInput from "@/components/PasswordInput";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Upload, Trash2, KeyRound } from "lucide-react";
import { SPORTS as SPORTS_FALLBACK, ATHLETE_CLASSES } from "@/lib/sports";

const MAX_DOB = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

export default function EditProfileDialog({ open, onClose, onSaved }) {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", gender: "M",
    country: "", city: "", dob: "", gym: "",
    athlete_class: "regular", primary_sport: "",
    height_cm: "", weight_kg: "",
  });
  const [gyms, setGyms] = useState([]);
  const [sports, setSports] = useState(SPORTS_FALLBACK);
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);  // raw selected image dataURL
  const fileRef = useRef(null);
  // Password change
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const setPw = (k, v) => setPwForm((f) => ({ ...f, [k]: v }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open || !user) return;
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      gender: user.gender || "M",
      country: user.country || "",
      city: user.city || "",
      dob: (user.dob || "").slice(0, 10),
      gym: user.gym || "",
      athlete_class: user.athlete_class || "regular",
      primary_sport: user.primary_sport || "",
      height_cm: user.height_cm != null ? String(user.height_cm) : "",
      weight_kg: user.weight_kg != null ? String(user.weight_kg) : "",
    });
    api.get("/gyms").then((r) => setGyms(r.data.gyms || [])).catch(() => {});
    api.get("/sports").then((r) => setSports(r.data.sports || SPORTS_FALLBACK)).catch(() => {});
    setAvatarPreview(user.avatar_url || null);
    setPwForm({ current_password: "", new_password: "", confirm_password: "" });
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open so the dialog (not the page) handles scrolling
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB before crop");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadCropped = async (croppedDataUrl) => {
    setAvatarBusy(true);
    try {
      await api.post("/me/avatar", { data_url: croppedDataUrl });
      setAvatarPreview(croppedDataUrl);
      setCropSrc(null);
      await refresh();
      toast.success("PROFILE PHOTO UPDATED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete("/me/avatar");
      setAvatarPreview(null);
      await refresh();
      toast.success("PROFILE PHOTO REMOVED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAvatarBusy(false);
    }
  };

  const changePassword = async (e) => {
    e?.preventDefault?.();
    if (!pwForm.current_password) { toast.error("Current password required"); return; }
    if (pwForm.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error("New password and confirmation do not match"); return; }
    if (pwForm.new_password === pwForm.current_password) { toast.error("New password must be different from current"); return; }
    setPwBusy(true);
    try {
      const { data } = await api.post("/me/password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      if (data?.access_token) {
        localStorage.setItem("gps_token", data.access_token);
      }
      toast.success("PASSWORD UPDATED");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPwBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.dob) {
      toast.error("Date of birth is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        gender: form.gender,
        country: form.country.trim(),
        city: form.city.trim(),
        gym: form.gym.trim(),
        dob: form.dob,
        athlete_class: form.athlete_class,
        primary_sport: form.primary_sport || "",
      };
      // Height / Weight are optional in the profile — send only if set.
      if (form.height_cm !== "") {
        const h = parseFloat(form.height_cm);
        if (Number.isNaN(h) || h < 80 || h > 250) {
          toast.error("Height must be between 80 and 250 cm");
          setBusy(false);
          return;
        }
        payload.height_cm = h;
      }
      if (form.weight_kg !== "") {
        const w = parseFloat(form.weight_kg);
        if (Number.isNaN(w) || w < 15 || w > 250) {
          toast.error("Weight must be between 15 and 250 kg");
          setBusy(false);
          return;
        }
        payload.weight_kg = w;
      }
      await api.patch("/me/profile", payload);
      await refresh();
      toast.success("PROFILE UPDATED");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto overscroll-contain"
        onClick={onClose}
        data-testid="edit-profile-dialog"
      >
        <div className="min-h-full flex items-start justify-center pt-20 sm:pt-24 pb-8 px-4 sm:px-6">
          <GlassPanel
            className="p-6 sm:p-8 w-full max-w-2xl relative my-4 sm:my-8"
            scanline
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 grid place-items-center w-9 h-9 rounded-full border border-white/15 bg-black/40 text-mute hover:text-white hover:border-white/40 hover:bg-black/60 transition-colors z-10"
              data-testid="edit-profile-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="gps-tag mb-3">PROFILE</div>
            <h2 className="gps-heading text-2xl text-white">EDIT DETAILS</h2>
            <p className="text-mute text-sm mt-1">
              Update your identity, location, and institutional affiliation.
            </p>

            {/* Avatar */}
            <div className="mt-6 flex items-center gap-5" data-testid="edit-profile-avatar-row">
              <div className="w-20 h-20 rounded-full overflow-hidden border border-white/25 bg-black/40 grid place-items-center shrink-0">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="gps-mono text-mute text-[10px] tracking-[0.2em] text-center px-1">NO<br/>PHOTO</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="hidden"
                  data-testid="edit-profile-avatar-input"
                />
                <button type="button" onClick={onPickFile} disabled={avatarBusy} className="gps-btn ghost" data-testid="edit-profile-avatar-upload">
                  <Upload size={14} /> {avatarBusy ? "UPLOADING..." : "UPLOAD & CROP"}
                </button>
                {avatarPreview && (
                  <button type="button" onClick={removeAvatar} disabled={avatarBusy} className="gps-btn ghost" data-testid="edit-profile-avatar-remove">
                    <Trash2 size={14} /> REMOVE
                  </button>
                )}
                <div className="text-mute text-[10px] gps-mono tracking-[0.15em]">PNG / JPG · CROP & FIT</div>
              </div>
            </div>

            <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="gps-label">NAME / CALLSIGN</label>
                <input
                  className="gps-input"
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  data-testid="edit-profile-name"
                />
              </div>
              <div>
                <label className="gps-label">EMAIL</label>
                <input
                  type="email"
                  className="gps-input"
                  required
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  data-testid="edit-profile-email"
                />
              </div>
              <div>
                <label className="gps-label">PHONE</label>
                <input
                  type="tel"
                  className="gps-input"
                  required
                  pattern="[+0-9\s\(\)\-]{6,20}"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  data-testid="edit-profile-phone"
                />
              </div>
              <div>
                <label className="gps-label">GENDER</label>
                <select
                  className="gps-input"
                  value={form.gender}
                  onChange={(e) => set("gender", e.target.value)}
                  data-testid="edit-profile-gender"
                >
                  <option value="M">M</option>
                  <option value="F">F</option>
                </select>
              </div>
              <div>
                <label className="gps-label">DATE OF BIRTH</label>
                <input
                  type="date"
                  className="gps-input"
                  max={MAX_DOB}
                  value={form.dob}
                  onChange={(e) => set("dob", e.target.value)}
                  data-testid="edit-profile-dob"
                  aria-required="true"
                />
              </div>
              <div>
                <label className="gps-label">COUNTRY</label>
                <input
                  className="gps-input"
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                  data-testid="edit-profile-country"
                />
              </div>
              <div>
                <label className="gps-label">CITY</label>
                <input
                  className="gps-input"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  data-testid="edit-profile-city"
                />
              </div>
              <div>
                <label className="gps-label">ATHLETE CLASS</label>
                <select
                  className="gps-input"
                  value={form.athlete_class}
                  onChange={(e) => set("athlete_class", e.target.value)}
                  data-testid="edit-profile-athlete-class"
                >
                  {ATHLETE_CLASSES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="gps-label">PRIMARY SPORT</label>
                <select
                  className="gps-input"
                  value={form.primary_sport}
                  onChange={(e) => set("primary_sport", e.target.value)}
                  data-testid="edit-profile-primary-sport"
                >
                  <option value="">SELECT SPORT</option>
                  {sports.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="gps-label">HEIGHT (CM)</label>
                <input
                  className="gps-input"
                  type="number"
                  min="80"
                  max="250"
                  step="0.1"
                  placeholder="e.g. 178"
                  value={form.height_cm}
                  onChange={(e) => set("height_cm", e.target.value)}
                  data-testid="edit-profile-height"
                />
              </div>
              <div>
                <label className="gps-label">WEIGHT (KG)</label>
                <input
                  className="gps-input"
                  type="number"
                  min="15"
                  max="250"
                  step="0.1"
                  placeholder="e.g. 72"
                  value={form.weight_kg}
                  onChange={(e) => set("weight_kg", e.target.value)}
                  data-testid="edit-profile-weight"
                />
              </div>
              <div className="md:col-span-2">
                <label className="gps-label">INSTITUTION</label>
                <input
                  className="gps-input"
                  list="edit-gym-options"
                  placeholder="Pick or type your institution"
                  value={form.gym}
                  onChange={(e) => set("gym", e.target.value)}
                  data-testid="edit-profile-gym"
                />
                <datalist id="edit-gym-options">
                  {gyms.map((g) => <option key={g} value={g} />)}
                </datalist>
                <div className="text-[10px] gps-mono text-mute mt-1 tracking-[0.15em]">
                  CAN&apos;T FIND YOURS? TYPE IT IN — IT&apos;LL BE ADDED AS PENDING APPROVAL
                </div>
              </div>

              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="gps-btn ghost"
                  data-testid="edit-profile-cancel"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="gps-btn"
                  data-testid="edit-profile-save"
                >
                  {busy ? "SAVING..." : "SAVE CHANGES"}
                </button>
              </div>
            </form>

            {/* CHANGE PASSWORD */}
            <div className="mt-8 pt-6 border-t border-white/10" data-testid="change-password-section">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound size={16} className="text-cyan" />
                <div className="gps-label !text-cyan">CHANGE PASSWORD</div>
              </div>
              <div className="text-mute text-xs mb-4">
                Rotate your password. You&apos;ll stay signed in on this device after the change.
              </div>
              <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="gps-label">CURRENT PASSWORD</label>
                  <PasswordInput
                    required
                    autoComplete="current-password"
                    value={pwForm.current_password}
                    onChange={(e) => setPw("current_password", e.target.value)}
                    data-testid="pw-current"
                    testIdToggle="pw-current-toggle"
                  />
                </div>
                <div>
                  <label className="gps-label">NEW PASSWORD</label>
                  <PasswordInput
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={pwForm.new_password}
                    onChange={(e) => setPw("new_password", e.target.value)}
                    data-testid="pw-new"
                    testIdToggle="pw-new-toggle"
                  />
                  <div className="text-[10px] gps-mono text-mute mt-1 tracking-[0.15em]">MIN 8 CHARACTERS</div>
                </div>
                <div>
                  <label className="gps-label">CONFIRM NEW PASSWORD</label>
                  <PasswordInput
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={pwForm.confirm_password}
                    onChange={(e) => setPw("confirm_password", e.target.value)}
                    data-testid="pw-confirm"
                    testIdToggle="pw-confirm-toggle"
                  />
                </div>
                <div className="md:col-span-3 flex justify-end">
                  <button type="submit" disabled={pwBusy} className="gps-btn" data-testid="pw-submit">
                    <KeyRound size={14} /> {pwBusy ? "UPDATING..." : "UPDATE PASSWORD"}
                  </button>
                </div>
              </form>
            </div>
          </GlassPanel>
        </div>
      </div>

      <AvatarCropper
        open={!!cropSrc}
        src={cropSrc}
        busy={avatarBusy}
        onCancel={() => setCropSrc(null)}
        onConfirm={uploadCropped}
      />
    </>
  );
}
